import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

const MARGIN = 8;
const DRAG_THRESHOLD = 4;

export const unionRect = (rects) => {
    const present = rects.filter(Boolean);

    if (present.length === 0) {
        return null;
    }

    return {
        left: Math.min(...present.map(rect => rect.left)),
        top: Math.min(...present.map(rect => rect.top)),
        right: Math.max(...present.map(rect => rect.right)),
        bottom: Math.max(...present.map(rect => rect.bottom))
    };
};

export const clampDelta = (rect, dx, dy, width, height, margin = MARGIN) => {
    const minDx = margin - rect.left;
    const maxDx = Math.max(minDx, width - margin - rect.right);
    const minDy = margin - rect.top;
    const maxDy = Math.max(minDy, height - margin - rect.bottom);

    return {
        dx: Math.min(Math.max(dx, minDx), maxDx),
        dy: Math.min(Math.max(dy, minDy), maxDy)
    };
};

export const offsetStyle = ({x, y}) => (x || y ? {transform: `translate(${x}px, ${y}px)`} : {});

const useDraggableOffset = (watch) => {
    const handleRef = useRef(null);
    const panelRef = useRef(null);
    const [offset, setOffset] = useState({x: 0, y: 0});
    const drag = useRef(null);
    const moved = useRef(false);

    const measure = useCallback(() => unionRect([
        handleRef.current?.getBoundingClientRect() ?? null,
        panelRef.current?.getBoundingClientRect() ?? null
    ]), []);

    const keepInside = useCallback(() => {
        const rect = measure();

        if (!rect) {
            return;
        }

        const {dx, dy} = clampDelta(rect, 0, 0, window.innerWidth, window.innerHeight);

        if (dx !== 0 || dy !== 0) {
            setOffset(current => ({x: current.x + dx, y: current.y + dy}));
        }
    }, [measure]);

    useLayoutEffect(() => {
        keepInside();
    }, [keepInside, watch]);

    useEffect(() => {
        window.addEventListener('resize', keepInside);

        return () => window.removeEventListener('resize', keepInside);
    }, [keepInside]);

    const onPointerDown = (event) => {
        if (event.button !== 0) {
            return;
        }

        drag.current = {
            startX: event.clientX,
            startY: event.clientY,
            offset,
            rect: measure()
        };
        moved.current = false;
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
        const current = drag.current;

        if (!current || !current.rect) {
            return;
        }

        const rawDx = event.clientX - current.startX;
        const rawDy = event.clientY - current.startY;

        if (!moved.current && Math.hypot(rawDx, rawDy) < DRAG_THRESHOLD) {
            return;
        }

        moved.current = true;

        const {dx, dy} = clampDelta(current.rect, rawDx, rawDy, window.innerWidth, window.innerHeight);

        setOffset({x: current.offset.x + dx, y: current.offset.y + dy});
    };

    const onPointerUp = (event) => {
        drag.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
    };

    const onClickCapture = (event) => {
        if (moved.current) {
            event.preventDefault();
            event.stopPropagation();
            moved.current = false;
        }
    };

    return {
        handleRef,
        panelRef,
        style: offsetStyle(offset),
        handleProps: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
            onClickCapture
        }
    };
};

export default useDraggableOffset;
